import React, { useState } from 'react';
import {
    Paper,
    Box,
    Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from "@mui/material";
import { colors, typography } from "../../styles/globals.js";
import { mock_associated_people } from '../../mock.js';
import PersonCard from './PersonCard.js';
import _ from "lodash"
import { Typography } from '@mui/material';
import { formatAddress, formatBusinessName } from '../../utils/nameFormatters.js';
import { AddUBOButton, AddEntityButton, RemoveEntityButton, RemoveUBOButton, EditLinkedInButton, EditUBOButton } from '../EditMode/UBOEditControls';
import { useEditMode } from '../EditMode/EditModeContext';

const styles = {
    sectionTitle: {
        fontSize: typography.fontSizes.base,
        fontWeight: typography.fontWeights.semibold,
        color: colors.primary,
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    }
};

// Transform our mock data to match PersonCard interface
const transformPersonData = (person) => {
    // Agent type -> shows State + Rating
    if (person.type === 'agent') {
        return {
            name: person.name,
            title: person.role,
            state: 'Florida',
            addressStatus: {
                isResidential: person.riskLevel === 'medium',
                isActive: person.backgroundCheck === 'completed'
            }
        };
    }

    // CEO, executives, board members -> shows "Cited on website" fields
    return {
        name: person.name,
        title: person.role,
        citedOnWebsite: person.riskLevel === 'low',
        references: person.ownershipPercentage || 3,
        connectedAttributes: person.responsibilities.slice(0, 2), // Show first 2 responsibilities
        socialLinks: {
            linkedin: person.linkedIn
        },
        otherLinkedEntities: person.ownershipPercentage > 20 ? 5 : 2
    };
};
const AssociatedPeopleTab = ({ associated_people, graphData, onUpdateUbos, onUpdateGraph, onCombinedUpdate }) => {

    const [showAll, setShowAll] = React.useState(false);
    const { isEditMode, addChangeDescription } = useEditMode();
    const [editEntityDialog, setEditEntityDialog] = useState(null);
    const [editEntityForm, setEditEntityForm] = useState({});

    const handleRemoveEntityFromCard = (personNodeId, entity) => {
        if (!onCombinedUpdate) return;
        onCombinedUpdate(prev => {
            const updatedUbos = (prev.ubos || []).map(u => {
                if (u.node_id !== personNodeId) return u;
                return { ...u, associated_entities: (u.associated_entities || []).filter(ae => ae.business_entity_id !== entity.business_entity_id) };
            });
            const gd = prev.graph_data || {};
            return {
                ubos: updatedUbos,
                graph_data: {
                    ...gd,
                    nodes: (gd.nodes || []).filter(n => n.id !== entity.business_entity_id),
                    edges: (gd.edges || []).filter(e => {
                        const s = String(e.source?.id || e.source);
                        const t = String(e.target?.id || e.target);
                        return s !== entity.business_entity_id && t !== entity.business_entity_id;
                    }),
                },
            };
        });
        addChangeDescription(`Removed entity: ${entity.legal_name} from ${associated_people.find(u => u.node_id === personNodeId)?.name}`);
    };

    const handleEditEntityFromCard = (personNodeId, entity) => {
        setEditEntityDialog({ personNodeId, entity });
        setEditEntityForm({
            legal_name: entity.legal_name || '',
            state_code: entity.state_code || '',
            state_id: entity.state_id || '',
            status: entity.status || '',
            incorporation_date: entity.incorporation_date?.split('T')[0] || '',
        });
    };

    const handleSaveEntityEdit = () => {
        if (!editEntityDialog || !onCombinedUpdate) return;
        const { personNodeId, entity } = editEntityDialog;
        const statusLower = editEntityForm.status.toLowerCase().trim();
        const inactivePatterns = ['inactive', 'dissolved', 'revoked', 'expired', 'forfeited', 'withdrawn', 'merged', 'dead', 'suspended'];
        const isValid = !inactivePatterns.some(p => statusLower.includes(p)) && statusLower.length > 0;
        const personNodeId_ = `person_${personNodeId}`;
        const entityId = entity.business_entity_id;

        onCombinedUpdate(prev => {
            const updatedUbos = (prev.ubos || []).map(u => {
                if (u.node_id !== personNodeId) return u;
                return {
                    ...u,
                    associated_entities: (u.associated_entities || []).map(ae => {
                        if (ae.business_entity_id !== entityId) return ae;
                        return {
                            ...ae,
                            legal_name: editEntityForm.legal_name,
                            state_code: editEntityForm.state_code,
                            state_id: editEntityForm.state_id,
                            status: editEntityForm.status,
                            incorporation_date: editEntityForm.incorporation_date ? editEntityForm.incorporation_date + 'T00:00:00' : ae.incorporation_date,
                        };
                    }),
                };
            });

            const gd = prev.graph_data || {};
            const updatedNodes = (gd.nodes || []).map(n => {
                if (n.id !== entityId) return n;
                return { ...n, label: editEntityForm.legal_name, data: { ...n.data, state_code: editEntityForm.state_code, state_id: editEntityForm.state_id, status: editEntityForm.status, is_valid_status: isValid, incorporation_date: editEntityForm.incorporation_date ? editEntityForm.incorporation_date + 'T00:00:00' : n.data.incorporation_date } };
            });

            const edges = gd.edges || [];
            const hasEdge = edges.some(e => {
                const s = String(e.source?.id || e.source);
                const t = String(e.target?.id || e.target);
                return s === personNodeId_ && t === entityId;
            });
            const updatedEdges = hasEdge ? edges : [...edges, {
                label: 'member',
                type: 'associated_with',
                source: personNodeId_,
                target: entityId,
                confidence: 100.0,
            }];

            return {
                ubos: updatedUbos,
                graph_data: { ...gd, nodes: updatedNodes, edges: updatedEdges },
            };
        });
        addChangeDescription(`Edited entity: ${editEntityForm.legal_name}`);
        setEditEntityDialog(null);
    };

    // Handle the case where associated_people might be undefined or empty
    if (!associated_people || associated_people.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: colors.textSecondary }}>
                    No associated people found
                </Typography>
            </Box>
        );
    }

    // Sort people so input UBOs appear first
    const sortedPeople = _.sortBy(associated_people, (person) => {
        const isInputData = person.sources && person.sources.includes('INPUT_DATA');
        return isInputData ? 0 : 1;
    });

    // Calculate how many cards fit in first row based on container width
    // 4 cards per row looks better and fills the space more evenly
    const cardsPerRow = 4;
    const totalPeople = sortedPeople.length;
    const shouldShowButton = totalPeople > cardsPerRow;

    const displayedPeople = showAll ? sortedPeople : _.take(sortedPeople, cardsPerRow);

    const handleToggleView = () => {
        setShowAll(!showAll);
    };

    // Transform real data to match PersonCard interface
    const transformRealPersonData = (person) => {
        // For the new data structure, treat all as officers since we don't have agent type
        return {
            name: formatBusinessName(person.name),
            title: person.role ? person.role.replace(/,\s*/g, ' / ').replace(/\s+/g, ' ').trim().replace(/^\s*\/\s*/, '').replace(/\b\w/g, l => l.toUpperCase()) : '',
            citedOnWebsite: person.cited_on_website,
            references: person.online_references_count || 0,
            connectedAttributes: person.affiliated_addresses ? [`Address: ${formatAddress(person.affiliated_addresses[0])}`] : [],
            socialLinks: {
                linkedin: person.linkedin || null,
                facebook: person.facebook || null
            },
            otherLinkedEntities: person.associated_entities || [],
            source: _.map(person.sources || [], source => ({
                source_url: source,
                fetched_at: null // We don't have fetched_at data in this context
            }))
        };
    };

    return (
        <>
        <div>
            {onUpdateUbos && (
                <Box sx={{ mb: 2 }}>
                    <AddUBOButton ubos={associated_people} graphData={graphData} onUpdateUbos={onUpdateUbos} onUpdateGraph={onUpdateGraph} onCombinedUpdate={onCombinedUpdate} />
                </Box>
            )}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                rowGap: '32px', // Add spacing between rows
                columnGap: '12px' // Add spacing between columns
            }}>
                {_.map(displayedPeople, (person, index) => {
                    const transformedPerson = transformRealPersonData(person);
                    const variant = 'officer'; // All are officers in the real data

                    return (
                        <Box key={person.node_id || index} sx={{ position: 'relative' }}>
                            {onUpdateUbos && (
                                <Box sx={{ position: 'absolute', top: -8, right: -8, zIndex: 1, display: 'flex', gap: 0.5 }}>
                                    <RemoveUBOButton ubo={person} ubos={associated_people} graphData={graphData} onUpdateUbos={onUpdateUbos} onUpdateGraph={onUpdateGraph} onCombinedUpdate={onCombinedUpdate} />
                                </Box>
                            )}
                            <PersonCard
                                person={transformedPerson}
                                variant={variant}
                                onRemoveEntity={isEditMode && onUpdateUbos ? (entity) => handleRemoveEntityFromCard(person.node_id, entity) : undefined}
                                onEditEntity={isEditMode && onUpdateUbos ? (entity) => handleEditEntityFromCard(person.node_id, entity) : undefined}
                            />
                            {onUpdateUbos && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                    <EditUBOButton ubo={person} ubos={associated_people} graphData={graphData} onUpdateUbos={onUpdateUbos} onUpdateGraph={onUpdateGraph} onCombinedUpdate={onCombinedUpdate} />
                                    <AddEntityButton ubo={person} ubos={associated_people} graphData={graphData} onUpdateUbos={onUpdateUbos} onUpdateGraph={onUpdateGraph} onCombinedUpdate={onCombinedUpdate} />
                                    <EditLinkedInButton ubo={person} ubos={associated_people} graphData={graphData} onUpdateUbos={onUpdateUbos} onUpdateGraph={onUpdateGraph} onCombinedUpdate={onCombinedUpdate} />
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {shouldShowButton && (
                <Box sx={{ textAlign: 'center', marginTop: '32px' }}>
                    <Button
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        onClick={handleToggleView}
                    >
                        {showAll
                            ? `Show Less (${cardsPerRow} people)`
                            : `View All (${totalPeople} people)`
                        }
                    </Button>
                </Box>
            )}
        </div>
        {/* Edit Entity Dialog */}
        <Dialog open={!!editEntityDialog} onClose={() => setEditEntityDialog(null)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Entity</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                <TextField label="Legal Name" value={editEntityForm.legal_name || ''} onChange={(e) => setEditEntityForm(f => ({ ...f, legal_name: e.target.value }))} size="small" />
                <TextField label="State Code" value={editEntityForm.state_code || ''} onChange={(e) => setEditEntityForm(f => ({ ...f, state_code: e.target.value }))} size="small" />
                <TextField label="State ID" value={editEntityForm.state_id || ''} onChange={(e) => setEditEntityForm(f => ({ ...f, state_id: e.target.value }))} size="small" />
                <TextField label="Status" value={editEntityForm.status || ''} onChange={(e) => setEditEntityForm(f => ({ ...f, status: e.target.value }))} size="small" />
                <TextField label="Incorporation Date" value={editEntityForm.incorporation_date || ''} onChange={(e) => setEditEntityForm(f => ({ ...f, incorporation_date: e.target.value }))} size="small" placeholder="YYYY-MM-DD" />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEditEntityDialog(null)}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveEntityEdit}>Save</Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default AssociatedPeopleTab;